import { useApk, useBuildApk } from '../api/apk.js';
import { Alert, Button } from './ui.js';

/** Steps for sideloading the APK onto a real Android phone. */
function InstallGuide() {
  return (
    <details className="mt-3 rounded-md bg-neutral/40 p-3 text-sm">
      <summary className="cursor-pointer font-medium">
        How to install it on an Android phone
      </summary>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        <li>On your Android phone, open this page and tap “Download the app”.</li>
        <li>
          When it asks, allow your browser to install apps (Android opens a setting — turn on “Allow
          from this source” for your browser). This is needed because the app is installed directly
          rather than from the Play Store.
        </li>
        <li>
          Open the downloaded “.apk” file (from the notification or your Downloads) and tap Install.
        </li>
        <li>Open the app from your home screen — it runs your live app full-screen.</li>
      </ol>
      <p className="mt-2 text-xs opacity-70">
        This is a test build signed with a temporary key. If you rebuild it later, uninstall the old
        one first before installing the new one.
      </p>
    </details>
  );
}

/**
 * The project's Android app in the Live testing view. `canBuild` (staff) shows a
 * build/rebuild button; founders get a read-only download + guide once ready.
 */
export function AndroidAppCard({ projectId, canBuild }: { projectId: string; canBuild: boolean }) {
  const apk = useApk(projectId);
  const build = useBuildApk(projectId);
  const status = apk.data?.status ?? 'none';
  const building = status === 'building';

  return (
    <div className="rounded-md border border-secondary/20 p-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">Android app</h3>
        {canBuild ? (
          <Button
            variant="secondary"
            disabled={building || build.isPending}
            onClick={() => build.mutate()}
          >
            {building
              ? 'Building…'
              : build.isPending
                ? 'Requesting…'
                : status === 'ready'
                  ? 'Rebuild'
                  : 'Build Android app'}
          </Button>
        ) : null}
      </div>

      {status === 'ready' && apk.data?.downloadUrl ? (
        <div className="flex flex-col gap-1">
          <a
            href={apk.data.downloadUrl}
            className="inline-flex w-fit items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            ⬇ Download the app
          </a>
          {apk.data.builtAt ? (
            <p className="text-xs opacity-60">
              Built {new Date(apk.data.builtAt).toLocaleString()}
            </p>
          ) : null}
          <InstallGuide />
        </div>
      ) : building ? (
        <p className="text-sm opacity-70">
          Your Android app is being prepared — the download will appear here when it’s ready.
        </p>
      ) : status === 'failed' ? (
        <Alert kind="info">
          The Android app build didn’t complete{canBuild ? ' — try again.' : '. Our team is on it.'}
        </Alert>
      ) : (
        <p className="text-sm opacity-70">
          {canBuild
            ? 'Build a downloadable Android app that wraps this project’s live app.'
            : 'A downloadable Android version can be made available here on request.'}
        </p>
      )}

      {build.isError ? (
        <Alert>Could not start the build — the app needs to be deployed first.</Alert>
      ) : null}
    </div>
  );
}
